<template>
  <div class="chart-wrapper">
    <ChartHeader
      v-if="activityInfo.status === ACTIVITY_STATUS.PROCESSED"
      :current-metric="currentMetric"
      :hr-chart-unit="hrChartUnit"
      :change-hr-chart-unit="changeHrChartUnit" />

    <div v-if="activityInfo.type === SESSION_TYPES.INDIVIDUAL && activityInfo.status === ACTIVITY_STATUS.PROCESSED">
      <button 
        type="button" 
        class="btn btn-type-2 new-segment-btn" 
        @click.prevent="newSegmentInitPopup()">Create Segment</button>
    </div>

    <div 
      class="segments-filter-wrapper" 
      v-if="segments && segments.length && (currentMetric === sessionMetrics.TIME_ABOVE_80_PERCENT_HR || currentMetric === sessionMetrics.AP || currentMetric === sessionMetrics.WORKLOAD)">
      <b-dropdown id="ddown1" text="Segments" class="btn-group dropdown segments-filter-btn">
        <ul class="list-of-segments-dropdown">
          <li class="search-section">
            <div class="search-input-wrapper">
              <input class="form-control form-control-default search-input" v-model="searchSegmentName">
              <img class="search-icon" src="@/assets/images/icons/search-icon.png" alt="search-icon">
            </div>
          </li>
          <li>
          <ul id="listOfSegments" class="list-of-segments">
            <li role="menuitem">
              <div class="checkbox-wrapper">
                <input 
                  class="custom-checkbox"  
                  id="segment_select_all" 
                  type="checkbox" 
                  :checked="allSegmentsSelected"
                  @click.prevent="selectDeselectAll($event)" />
                <label for="segment_select_all"></label>
              </div>
              <label
                class="segment-name-label white-text"
                for="segment_select_all"
                @click.prevent="selectDeselectAll($event)">
                  {{ $t('CALENDAR_ACTIVITY.activityCharts.chartHeader.selectAllSegments')}}</label>
            </li>
              <template v-if="searchSegmentName">
                <li role="menuitem" v-for="segment in filteredSegmentsBySearch" :key="segment.id">
                  <div class="checkbox-wrapper">
                    <input 
                      class="custom-checkbox"  
                      :id="`segment_${segment.id}`" 
                      type="checkbox" 
                      :checked="segmentIsDisplayedOnChart(segment.id)"
                      @click.prevent="filterSegment(segment, $event)" />
                    <label 
                      :for="`segment_${segment.id}`"
                      @click.prevent="filterSegment(segment, $event)"></label>
                  </div>
                  <label
                    class="segment-name-label"
                    :for="`segment_${segment.id}`"
                    @click.prevent="filterSegment(segment, $event)">
                    {{segment.name || 'No name'}}</label>
                  <a 
                    v-if="activityInfo.type === SESSION_TYPES.INDIVIDUAL"
                    class="delete-btn" 
                    v-b-modal.deleteSegmentConfirmationModal
                    @click="segmentToDelete = segment">
                    <i class="fas fa-trash-alt" aria-hidden="true"></i>
                  </a>
                </li>
              </template>

              <template v-if="!searchSegmentName">
                <li role="menuitem" v-for="segment in segments" :key="segment.id">
                  <div class="checkbox-wrapper">
                    <input 
                      class="custom-checkbox"  
                      :id="`segment_${segment.id}`" 
                      type="checkbox" 
                      :checked="segmentIsDisplayedOnChart(segment.id)"
                      @click.prevent="filterSegment(segment, $event)" />
                    <label 
                      :for="`segment_${segment.id}`"
                      @click.prevent="filterSegment(segment, $event)"></label>
                  </div>
                  <label
                    class="segment-name-label"
                    :for="`segment_${segment.id}`"
                    @click.prevent="filterSegment(segment, $event)">
                    {{segment.name || 'No name'}}</label>
                  <a 
                    v-if="activityInfo.type === SESSION_TYPES.INDIVIDUAL"
                    class="delete-btn" 
                    v-b-modal.deleteSegmentConfirmationModal
                    @click="segmentToDelete = segment">
                    <i class="fas fa-trash-alt" aria-hidden="true"></i>
                  </a>
                </li>
              </template>
            </ul>
          </li>
        </ul>
      </b-dropdown>
    </div>
      
    <HRActivityChart 
      v-if="currentMetric === sessionMetrics.TIME_ABOVE_80_PERCENT_HR && activityInfo.status === ACTIVITY_STATUS.PROCESSED"
      :generate-bpm-chart-data="generateHRBpmData"
      :generate-percentage-chart-data="generateHRPercentageData"
      :duration="activityInfo.durationInSeconds"
      :hr-chart-unit="hrChartUnit"
      :segments="segments"
      :filtered-segments="filteredSegments"
      :create-activity-segment="createActivitySegment"
      :delete-segment="deleteSegment"
      :activity-id="playerActivityId" 
      :activity-type="activityInfo.type"
      :activity-info="activityInfo"
      :brush-creating-triggered="brushCreatingTriggered"
      :fetch-more-precise-activity-data="fetchMorePreciseActivityData"
      :medium-precise-chart-data-hr-percentage="mediumPreciseChartDataHrPercentage"
      :mediumPreciseChartDataHrBPM="mediumPreciseChartDataHrBPM"
      :highPreciseChartDataHrBPM="highPreciseChartDataHrBPM"
      :highPreciseChartDataHrPercentage="highPreciseChartDataHrPercentage"
      :highestPreciseChartDataHrBPM="highestPreciseChartDataHrBPM"
      :highestPreciseChartDataHrPercentage="highestPreciseChartDataHrPercentage"
      :zoomSelection="zoomSelection"
      :onUpdateSelection="onUpdateSelection" />

    <APActivityChart 
      v-if="currentMetric === sessionMetrics.AP && activityInfo.status === ACTIVITY_STATUS.PROCESSED"
      :generateChartData="generateAPData"
      :duration="activityInfo.durationInSeconds"
      :segments="segments"
      :filtered-segments="filteredSegments"
      :create-activity-segment="createActivitySegment"
      :delete-segment="deleteSegment"
      :activity-id="playerActivityId"
      :activity-type="activityInfo.type"
      :activity-info="activityInfo"
      :brush-creating-triggered="brushCreatingTriggered"
      :fetch-more-precise-activity-data="fetchMorePreciseActivityData"
      :mediumPreciseChartDataAP="mediumPreciseChartDataAP"
      :highPreciseChartDataAP="highPreciseChartDataAP"
      :highestPreciseChartDataAP="highestPreciseChartDataAP"
      :zoomSelection="zoomSelection"
      :onUpdateSelection="onUpdateSelection" />

    <ActivityDistanceChart 
      v-if="activityInfo.status === ACTIVITY_STATUS.PROCESSED"
      :data="generatedDistanceData"
      :max-value="distanceMaxValue"
      :ticks="distanceTicks"
      :tick-unit="'m'"
      :data-unit="distanceDataUnit"
      :percentage="distancePercentage" 
      :max-percentage-value="100"
      :chart-axis-label="$t('CALENDAR_ACTIVITY.activityCharts.activityDistanceChart.chartAxisLabel')"
      :activity-info="activityInfo"
      :segments="filteredSegments"
      :zoomSelection="zoomSelection"
      :onUpdateSelection="onUpdateSelection"
      :current-metric="currentMetric"
      :distance-chart-unit="distanceChartUnit"
      :change-distance-chart-unit="changeDistanceChartUnit" 
      :buildImageUrlAndLog="buildImageUrlAndLog"/>


      <button v-if="(currentMetric === sessionMetrics.TIME_ABOVE_80_PERCENT_HR && activityInfo.status === ACTIVITY_STATUS.PROCESSED) || currentMetric === sessionMetrics.AP && activityInfo.status === ACTIVITY_STATUS.PROCESSED"  
      class="btn btn-type-2 no-outline download-image uppercase" 
      @click="buildImageUrlAndLog" >{{ $t('CALENDAR_ACTIVITY.activityCharts.saveChartAsImage')}}</button>

      <div class="d-none" id="newSvgContainer"></div>
      <div class="d-none" id="canvasContainer"></div>

    <b-modal 
      ref="deleteSegmentConfirmationModal" 
      id="deleteSegmentConfirmationModal">
      <div slot="modal-header">
        <span class="modal-title">
          {{ $t('CALENDAR_ACTIVITY.activityCharts.deleteSegment.title')}}
        </span>
      </div>

      {{ $t('CALENDAR_ACTIVITY.activityCharts.deleteSegment.modalConfirmation')}}

      <div slot="modal-footer" class="w-100">
        <button type="button" class="btn btn-type-3" @click="closeDeleteConfirmationModal">{{ $t('CALENDAR_ACTIVITY.activityCharts.deleteSegment.cancel')}}</button>
        <button type="button" class="btn btn-type-4" @click="deleteSegmentOnClick">{{ $t('CALENDAR_ACTIVITY.activityCharts.deleteSegment.confirm')}}</button>
      </div>
    </b-modal>

    <MakeSegmentModal 
      :selection="selection"
      :show-modal="showModal"
      :max-value="Math.round(activityInfo.durationInSeconds/60)"
      :creation-commited="creationCommited"
      :creation-canceled="creationCanceled"
      :segment-number="segments.length ? segments.length + 1 : 1" 
      :modal-hidden="modalHidden"
      :loading-form="loadingSegmentModalBtn" />
  </div>
</template>

<script>
import { SESSION_TYPES } from '@/constants/SessionConsts';
import { ACTIVITY_STATUS } from '@/constants/SessionConsts';
import _ from 'lodash';
import MakeSegmentModal from '@/components/calendar-activity/MakeSegmentModal';
import ActivityDistanceChart from '@/components/calendar-activity/ActivityDistanceChart'
import HRActivityChart from '@/components/calendar-activity/HRActivityChart'
import APActivityChart from '@/components/calendar-activity/APActivityChart'
import ChartHeader from '@/components/calendar-activity/ChartHeader'
import { TOASTER_SUCCESS } from "@/common/events.types";
import { 
  SESSION_METRICS, 
  TIME_ABOVE_80_PERCENT_HR_UNITS,
  DISTANCE_ZONES_UNITS
} from '@/constants/SessionConsts'

export default {
  name: 'ActivityCharts',
  props: {
    activityInfo : Object,
    data : Object,
    summary : Object,
    currentMetric : String,
    hrSelectedUnit : String,
    hrChartUnit : String,
    distanceChartUnit : String,
    changeHrChartUnit : Function,
    changeDistanceChartUnit : Function,
    segments : Array,
    filteredSegments : Array,
    createActivitySegment : Function,
    deleteSegment : Function,
    playerActivityId : Number,
    addToFilteredSegments : Function,
    removeFromFilteredSegments : Function,
    selectDeselectAllSegments : Function,
    fetchMorePreciseActivityData : Function,
    mediumPreciseChartDataHrPercentage : Object,
    mediumPreciseChartDataHrBPM : Object,
    highPreciseChartDataHrBPM : Object,
    highPreciseChartDataHrPercentage : Object,
    highestPreciseChartDataHrBPM : Object,
    highestPreciseChartDataHrPercentage : Object,
    mediumPreciseChartDataAP : Object,
    highPreciseChartDataAP : Object,
    highestPreciseChartDataAP : Object
  },
  
  data () {
    return {
      showModal: false,
      sessionMetrics: SESSION_METRICS,
      vAxisMaxMinHrBpm: { max: 260, min: 40 },
      vAxisMaxMinHrPercentage: { max: 110, min: 30 },
      vAxisMaxMinAP: { max: 12, min: 0 },
      chartColors: [],
      hrChartUnits: TIME_ABOVE_80_PERCENT_HR_UNITS,
      distanceUnits: DISTANCE_ZONES_UNITS,
      searchSegmentName: '',
      allSegmentsSelected: true,
      filteredSegmentsBySearch: [],
      segmentToDelete: null,
      selection: null,
      SESSION_TYPES: SESSION_TYPES,
      ACTIVITY_STATUS: ACTIVITY_STATUS,
      loadingSegmentModalBtn: false,
      zoomSelection: [0, this.activityInfo.durationInSeconds]
    }
  },
  computed: {
    generatedDistanceData () {
      const data = []

      if (this.distanceChartUnit === this.distanceUnits.PERCENTAGE) {
        data.push({ 
          zone1:  this.data.DISTANCE.distanceZone1Percentage,
          zone2:  this.data.DISTANCE.distanceZone2Percentage,
          zone3:  this.data.DISTANCE.distanceZone3Percentage,
          zone4:  this.data.DISTANCE.distanceZone4Percentage,
          value: this.data.DISTANCE.distance,
          title: `${this.activityInfo.playerFirstName} ${this.activityInfo.playerLastName}`,
          dataPoints: this.data.WORKLOAD.distanceDataPoints,
          label: `${this.activityInfo.playerFirstName} ${this.activityInfo.playerLastName}`,
          color: '#6F34AC',
          isMain: true
        })
      } else {
        data.push({ 
          zone1:  this.data.DISTANCE.distanceZone1,
          zone2:  this.data.DISTANCE.distanceZone2,
          zone3:  this.data.DISTANCE.distanceZone3,
          zone4:  this.data.DISTANCE.distanceZone4,
          value: this.data.DISTANCE.distance,
          title: `${this.activityInfo.playerFirstName} ${this.activityInfo.playerLastName}`,
          dataPoints: this.data.WORKLOAD.distanceDataPoints,
          label: `${this.activityInfo.playerFirstName} ${this.activityInfo.playerLastName}`,
          color: '#6F34AC',
          isMain: true
        })
      }

      return data
    },
    
    displayDistanceChart () {
      return this.data.DISTANCE.showDistanceChart;
    },
    
    distanceMaxValue () {
      return this.data.DISTANCE.distance
    },
    distanceTicks () {
      const numberOfCellsOnDistanceChart = 6
      const distanceAxisSteps = this.data.DISTANCE.distance / numberOfCellsOnDistanceChart
      const ticks = []

      for(var i = 0; i < numberOfCellsOnDistanceChart; i++) {
        ticks.push(parseInt(i * distanceAxisSteps));
      }
      ticks.push(this.data.DISTANCE.distance)
      
      return ticks
    },
    distanceDataUnit () {
      return this.distanceChartUnit === this.distanceUnits.PERCENTAGE ? '%' : 'm'
    },
    distancePercentage () {
      return this.distanceChartUnit === this.distanceUnits.PERCENTAGE
    },
  },
  methods: {
    getTooltipHtmlContent (minutes, value, displayLabel) {
      let html = '';
      let unit
      
      if (this.currentMetric === this.sessionMetrics.TIME_ABOVE_80_PERCENT_HR) {
        unit = this.hrChartUnit === this.hrChartUnits.PERCENTAGE ? '%' : 'BPM'
      } else {
        unit = 'MET'
      }

      html += '<div class="chart-tooltip-wrapper">';
      html += '<div class="info-wrapper">' + displayLabel + '</div>'
      html += '<div class="info-wrapper">' + minutes + ' min</div>'
      html += '<div class="info-wrapper">' + value + ' ' + unit + ' </div>'
      html += '</div>';

      return html;
    },
    generateHRBpmData () {
      return this.generateChartData(this.data.TIME_ABOVE_80_PERCENT_HR.bpmdataPoints, this.data.TIME_ABOVE_80_PERCENT_HR.started, 'HR')
    },
    generateHRPercentageData () {
      return this.generateChartData(this.data.TIME_ABOVE_80_PERCENT_HR.percentageDataPoints, this.data.TIME_ABOVE_80_PERCENT_HR.started, 'HR')
    },
    generateAPData () {
      return this.generateChartData(this.data.AP.dataPoints, this.data.AP.started, 'AP')
    },
    generateChartData (data, started, chartType) {
      let formattedData = [];
      let playerFullName = this.activityInfo.playerFirstName + ' ' + (this.activityInfo.playerMiddleName || '') + ' ' + (this.activityInfo.playerLastName || '');

      formattedData.push({
        label: playerFullName,
        color: chartType === 'HR' ? '#C93434' : '#AF852A',
        isMain: true,
        dataPoints: data,
        playerId: this.activityInfo.playerId
      });

      return formattedData;
    },
    segmentIsDisplayedOnChart (segmentId) {
      let displaySegment = false;

      for(let i = 0; i < this.filteredSegments.length; i++) {
        if(this.filteredSegments[i].id === segmentId) {
          displaySegment = true;
          break;
        }
      }

      return displaySegment;
    },
    filterSegment (segment, event) {
      let inputIsChecked = true; 
      let isInFilteredArr = this.filteredSegments.filter(s => s.id === segment.id).length;

      if(isInFilteredArr) {
        inputIsChecked = false;
      }

      if(inputIsChecked) {
        this.addToFilteredSegments({ segment });
      } else {
        this.removeFromFilteredSegments({ segmentId: segment.id });
        this.allSegmentsSelected = false;
      }
    },
    selectDeselectAll (event) {
      this.allSegmentsSelected = !this.allSegmentsSelected;

      this.selectDeselectAllSegments(this.allSegmentsSelected);
    },
    closeDeleteConfirmationModal () {
      this.$refs.deleteSegmentConfirmationModal.hide();
    },
    deleteSegmentOnClick () {
      this.deleteSegment({
        activityId: this.playerActivityId,
        segmentId: this.segmentToDelete.id
      }).then(r => {
        this.$eventHub.$emit(TOASTER_SUCCESS, {
          message: this.$t('TOAST_MESSAGES.deletedSegment')
        });
        this.$refs.deleteSegmentConfirmationModal.hide();
      })
    },
    onBrushCreation (selection) {
      this.showModal = true;
      this.selection = selection;
    },
    creationCommited (data) {
      this.loadingSegmentModalBtn = true;

      this.createActivitySegment({
        activityId: this.playerActivityId,
        data,
        metricsType: this.currentMetric
      }).then(r => {
        this.showModal = false;
        this.$eventHub.$emit(TOASTER_SUCCESS, {
          message: this.$t('TOAST_MESSAGES.segmentCreated')
        })

        this.loadingSegmentModalBtn = false;
      })
      .catch(err => {
        this.showModal = false;
        this.loadingSegmentModalBtn = false;
      })
    },
    creationCanceled () {
      this.$eventHub.$emit('remove-brushes-on-calendar-activity-chart');
    },
    modalHidden () {
      this.showModal = false;
    },
    newSegmentInitPopup () {
      this.onBrushCreation({
        name: `Segment ${this.segments.length}`,
        start: 0,
        end: 0
      });
    },
    brushCreatingTriggered (selection) {
      this.onBrushCreation(selection)
    },
    onUpdateSelection (selection) {
      this.zoomSelection = selection;
    },

    buildImageUrlAndLog() {
      this.$amplitude.logEvent('pwa: save activity chart as image', {currentMetric: this.currentMetric})
      const contextScope = this
      let svg = document.querySelector('#d3-js'),
          w = parseFloat(svg.getAttribute('width')),
          h = parseFloat(svg.getAttribute('height')),
          ratio = 2,
          canvas = document.createElement('canvas');
      canvas.id = "canvas";
      canvas.width  = w * ratio;
      canvas.height = h * ratio;

      let canvasContainer = document.querySelector('#canvasContainer');
      canvasContainer.appendChild(canvas);

      let newSvgCopy = document.createElementNS("http://www.w3.org/2000/svg","svg");
      newSvgCopy.id = 'newsvg';
      newSvgCopy.setAttribute('width', w);
      newSvgCopy.setAttribute('height', h);
      newSvgCopy.style.background = "white";
      newSvgCopy.innerHTML = document.querySelector('#d3-js>g').outerHTML;
      let gridElements = newSvgCopy.getElementsByClassName("grid");
       
      for(let i = 0; i < gridElements.length; i++) {
          gridElements[i].style.color = "#a5a5a5"
        } 

      let newSvgContainer = document.querySelector('#newSvgContainer');
      newSvgContainer.appendChild(newSvgCopy);

      let html = new XMLSerializer().serializeToString(newSvgContainer.querySelector('svg')),
          imgsrc = 'data:image/svg+xml;utf8,' + encodeURIComponent(html), // this is only possible for svg
          context = canvas.getContext("2d"),
          canvasdata,
          image = new Image();          

      image.onload = function() {
        context.drawImage(image, 0, 0, canvas.width , canvas.height);
        if (canvas.msToBlob) { //for IE
          let blob = canvas.msToBlob()
          window.navigator.msSaveBlob(blob, "output.jpg")
        } else {
          canvasdata = canvas.toDataURL("image/jpg",1);
        }
        let a = document.createElement("a");
        a.id = "imagepng"
        a.download = contextScope.activityInfo.playerFirstName + " " + contextScope.activityInfo.playerLastName + " " + contextScope.currentMetric;
        a.href = canvasdata;
        contextScope.raiseEvent(a, 'click');
      }
      // image.onerror = function(event) {
      //   console.log(event);
      // }
        image.src = imgsrc;
        newSvgContainer.removeChild(newSvgContainer.childNodes[0]);
        canvasContainer.removeChild(canvasContainer.childNodes[0]);
    },

    raiseEvent(element, eventType) {
      var eventRaised;  // event object
      // determine how and create the event
      if (document.createEvent) {
        eventRaised = document.createEvent('MouseEvents');
        eventRaised.initEvent(eventType, true, false);
        element.dispatchEvent(eventRaised);
      } else if (document.createEventObject) {
        eventRaised = document.createEventObject();
        element.fireEvent('on' + eventType, eventRaised);
      }
    }
  },
  watch: {
    searchSegmentName: function (newValues) {
      if(newValues) {
        this.filteredSegmentsBySearch = this.segments
        .filter(s => s.name.toLowerCase().indexOf(newValues.toLowerCase()) > -1);
      } else {
        this.filteredSegmentsBySearch = _.clone(this.segments);
      }
    }
  },
  components: {
    HRActivityChart,
    APActivityChart,
    ActivityDistanceChart,
    ChartHeader,
    MakeSegmentModal
  }
}
</script>

<style lang="scss" scoped>
  .activity-chart-wrapper {
    margin-bottom: 30px;
    height: 550px;

    &.no-data {
        display: table;
        width: 100%;
        background-color: $wood-smoke-2;

        .no-data-message {
            display: table-cell;
            vertical-align: middle;
            text-align: center;
            color: $iron-gray;
            font-size: 22px;
        }
    }
  }

  .new-segment-btn {
    margin-bottom: 20px;
    display: none;
  }

  .list-of-segments-dropdown {
    padding: 5px;
    padding-bottom: 0;
    margin-bottom: 0;

    li {
      list-style-type: none;
    }

    .segment-name-label {
      color: $iron-gray;
      font-weight: 500;
      font-size: 14px;
      margin-bottom: 0;
      vertical-align: top;
      cursor: pointer;
      width: calc(100% - 65px);
      white-space: nowrap; 
      overflow: hidden;
      text-overflow: ellipsis;

      &:hover {
        color: $white;
      }

      &.white-text {
        color: $white;
      }
    }

    .list-of-segments {
      padding: 10px;

      > li {
        margin-bottom: 10px;
        border-bottom: 1px solid $tundora;
        list-style-type: none;

        .checkbox-wrapper {
          label {
            margin-bottom: 17px;
          }
        }

        &:last-of-type {
          border-bottom: none;
          margin-bottom: 0;
        }

        .delete-btn {
          float: right;
          clear: both;
          vertical-align: top;
          padding: 0;
          color: $flush-mahogany;
          opacity: 0.5;
          margin-right: 10px;
          vertical-align: top;

          &:hover {
            opacity: 1;
          }
        }
      }
    }

    .checkbox-wrapper {
      display: inline-block;
    }

    .search-section {
      height: 50px;
    }

    .search-input-wrapper {
      .search-input {
        background-color: $shark-gray-2;

        &:hover {
          background-color: $shark-gray-4;
        }
      }
    }
  }

  .segments-filter-wrapper {
    display: inline-block;
    min-width: 300px;
  }

  .download-image {
    margin-bottom: 20px;
    font-size: 15px;
    text-transform: uppercase;
  }
</style>
